import React, { useState, useContext, useEffect } from "react";
import ReactTooltip from "react-tooltip";
import "./LayoutContent.css";
import StatusAccordion from "./StatusBar/StatusAccordion";

const LayoutContext = React.createContext<Context>({
  headerHeight: 0,
  subMenuWidth: 0,
  commandWidth: 0,
  setHeaderHeight: () => {},
  setSubMenuWidth: () => {},
  setCommandWidth: () => {},
});

type Props = {
  style?: React.CSSProperties;
  children?: any;
  showStatusAccordion?: boolean;
};

type Context = {
  headerHeight: number;
  subMenuWidth: number;
  commandWidth: number;
  setHeaderHeight: (height: number) => void;
  setSubMenuWidth: (width: number) => void;
  setCommandWidth: (width: number) => void;
};

function isHaveChildren(children: any) {
  let result: Boolean = false;
  if (Array.isArray(children)) {
    children.forEach((child) => {
      if (child) {
        result = true;
      }
    });
  } else if (children) {
    result = true;
  }

  return result;
}

function LayoutContent({ style, children, showStatusAccordion = true }: Props) {
  const header = children.filter((x: any) => x.type === Header)[0];
  const subMenu = children.filter((x: any) => x.type === SubMenu)[0];
  const command = children.filter((x: any) => x.type === CommandPanel)[0];
  const headerChild = header ? header.props.children : null;
  const subMenuChild = subMenu ? subMenu.props.children : null;
  const commandChild = command ? command.props.children : null;
  const statusAccordionHeight = showStatusAccordion ? 72 : 0;
  const [headerHeight, setHeaderHeight] = useState(headerChild ? 90 : 0);
  const [subMenuWidth, setSubMenuWidth] = useState(subMenuChild ? 66 : 0);
  const [commandWidth, setCommandWidth] = useState(commandChild ? 570 : 0);
  const width = commandWidth + subMenuWidth;
  const context: Context = {
    headerHeight: headerHeight,
    subMenuWidth: subMenuWidth,
    commandWidth: commandWidth,
    setHeaderHeight: setHeaderHeight,
    setSubMenuWidth: setSubMenuWidth,
    setCommandWidth: setCommandWidth,
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  useEffect(() => {
    if (!command) {
      setCommandWidth(0);
    }
  }, [command]);

  useEffect(() => {
    if (!subMenu) {
      setSubMenuWidth(0);
    }
  }, [subMenu]);

  useEffect(() => {
    if (!header) {
      setSubMenuWidth(0);
    }
  }, [header]);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "grid",
        gridTemplateAreas: `'header header header'
                          'submenu content command'
                          'submenu statusAccordion statusAccordion'
                          `,
        gridTemplateColumns: `${subMenuWidth}px calc(100% - ${width}px) ${commandWidth}px`,
        gridTemplateRows: `${headerHeight}px calc(100% - ${headerHeight}px - ${statusAccordionHeight}px) ${statusAccordionHeight}px`,
        ...style,
      }}
    >
      <LayoutContext.Provider value={context}>
        {children}
        {showStatusAccordion && (
          <div style={{ gridArea: "statusAccordion", zIndex: 100 }}>
            <StatusAccordion />
          </div>
        )}
      </LayoutContext.Provider>
      <ReactTooltip
        id="common-tooltip"
        className="common-tooltip"
        backgroundColor="#E38006"
        textColor="#FFFFFF"
        border={false}
      />
      <ReactTooltip
        id="gray-tooltip"
        className="gray-tooltip"
        backgroundColor="#5C5C5C"
        textColor="#FFFFFF"
        border={false}
      />
    </div>
  );
}

function useLayoutContext(): Context {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error(
      `Layout compound components cannot be rendered outside the Layout Content Component`
    );
  }

  return context;
}

function Header({ style, children }: Props) {
  const { setHeaderHeight } = useLayoutContext();

  let height = 0;

  if (isHaveChildren(children)) {
    height = 90;
  }

  if (style != null && style.height != null) {
    height = Number.parseInt(style.height.toString());
  }

  useEffect(() => {
    setHeaderHeight(height);
  }, [height, setHeaderHeight]);

  return (
    <div
      style={{
        gridArea: "header",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Content({ style, children }: Props) {
  const { subMenuWidth } = useLayoutContext();

  return (
    <div
      style={{
        gridArea: "content",
        height: "100%",
        borderTop: "#828282 6px solid",
        padding: 4,
        borderLeft:
          subMenuWidth === 0 ? "#828282 6px solid" : "#828282 0px solid",
        backgroundColor: "#FFFFFF",
        overflow: "auto",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SubMenu({ style, children }: Props) {
  const { setSubMenuWidth } = useLayoutContext();
  let width = 0;

  if (isHaveChildren(children)) {
    width = 65;
  }

  if (style != null && style.width != null) {
    width = Number.parseInt(style.width.toString());
  }

  useEffect(() => {
    setSubMenuWidth(width);
  }, [width, setSubMenuWidth]);

  return <div style={{ gridArea: "submenu" }}>{children}</div>;
}

function CommandPanel({ style, children }: Props) {
  const { setCommandWidth } = useLayoutContext();
  let width = 0;

  if (isHaveChildren(children)) {
    width = 550;
  }

  if (style != null && style.width != null) {
    width = Number.parseInt(style.width.toString());
  }

  useEffect(() => {
    setCommandWidth(width);
  }, [width, setCommandWidth]);

  return (
    <div
      style={{
        background: "white",
        borderTop: "#828282 6px solid",
        zIndex: 1,
        gridArea: "command",
        boxSizing: "border-box",
        WebkitBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        MozBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

LayoutContent.Header = Header;
LayoutContent.Content = Content;
LayoutContent.SubMenu = SubMenu;
LayoutContent.CommandPanel = CommandPanel;

export default LayoutContent;
